import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import ModuleLayout from "./ModuleLayout/ModuleLayout";
import { vehicleValidationSchema } from "./FormModal/validationSchemas";
import { useVinDecoder } from "../hooks/useVinDecoder";
import {
  buildVehicleCustomerOptions,
  buildVehiclePayload,
  createVehicleInitialValues,
  getVehicleFormFields,
  mapVinDecodedVehicle,
  prepareVehicleFormValues,
} from "./Vehicles/vehicleFormConfig";

function Vehicles() {
  const navigate = useNavigate();

  // Vehicle initial values
  const vehicleInit = createVehicleInitialValues(Helpers.authParentId);

  const [customers, setCustomers] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [onlyFillEmptyVin, setOnlyFillEmptyVin] = useState(true);
  const formikValuesRef = useRef({});
  const formikSetFieldValueRef = useRef(null);
  const lastDecodedVinRef = useRef("");
  const lastDecodedVehicleRef = useRef(null);

  const clearDecodedVehicleFieldsIfNeeded = (nextVin) => {
    const normalizedVin = String(nextVin || "").trim().toUpperCase();
    const lastDecodedVin = String(lastDecodedVinRef.current || "")
      .trim()
      .toUpperCase();

    if (!lastDecodedVin || normalizedVin === lastDecodedVin) {
      return;
    }

    const setFieldValue = formikSetFieldValueRef.current;
    const values = formikValuesRef.current || {};
    const lastDecodedVehicle = lastDecodedVehicleRef.current;

    if (!setFieldValue || !lastDecodedVehicle) {
      lastDecodedVinRef.current = "";
      lastDecodedVehicleRef.current = null;
      return;
    }

    ["year", "make", "model", "engine_size", "engine_cylinders", "drivetrain"].forEach(
      (field) => {
        if ((values?.[field] || "") === (lastDecodedVehicle?.[field] || "")) {
          setFieldValue(field, "");
        }
      },
    );

    lastDecodedVinRef.current = "";
    lastDecodedVehicleRef.current = null;
  };

  const handleVinDecoded = (mapped, onlyFillEmpty, sourceVin = "") => {
    const setFieldValue = formikSetFieldValueRef.current;
    const values = formikValuesRef.current || {};
    if (!setFieldValue) return;

    const normalizedDecodedVehicle = mapVinDecodedVehicle(mapped, {}, false);
    const nextValues = mapVinDecodedVehicle(mapped, values, onlyFillEmpty);

    setFieldValue("year", nextValues.year);
    setFieldValue("make", nextValues.make);
    setFieldValue("model", nextValues.model);
    setFieldValue("engine_size", nextValues.engine_size);
    setFieldValue("engine_cylinders", nextValues.engine_cylinders);
    setFieldValue("drivetrain", nextValues.drivetrain);
    lastDecodedVinRef.current = String(sourceVin || "").trim().toUpperCase();
    lastDecodedVehicleRef.current = normalizedDecodedVehicle;
  };

  const { vinLoading, vinError, onlyFillEmptyRef, onVinChange, onVinBlur } =
    useVinDecoder(handleVinDecoded, {
      baseUrl: Helpers.baseUrl,
      axiosConfig: Helpers.headers,
      endpointPrefix: "vin/",
    });

  useEffect(() => {
    onlyFillEmptyRef.current = onlyFillEmptyVin;
  }, [onlyFillEmptyVin, onlyFillEmptyRef]);

  useEffect(() => {
    const getCustomers = async () => {
      try {
        const response = await axios.get(
          `${Helpers.baseUrl}/customers/all/${Helpers.authParentId}`,
          Helpers.headers,
        );

        const customersData = Array.isArray(response.data) ? response.data : [];
        setCustomers(customersData);
        setCustomerOptions(buildVehicleCustomerOptions(customersData));
        setCustomersLoaded(true);
      } catch (error) {
        Helpers.unauthenticated(error, navigate);
        setCustomersLoaded(true);
      }
    };

    getCustomers();
  }, [navigate]);

  // Vehicle form fields - dynamically uses customerOptions
  const vehicleFields = getVehicleFormFields({
    customerOptions,
    customersLoaded,
    onlyFillEmptyVin,
    setOnlyFillEmptyVin,
    onlyFillEmptyRef,
    onVinInputChange: clearDecodedVehicleFieldsIfNeeded,
    onVinChange,
    onVinBlur,
    vinLoading,
    vinError,
    formikValuesRef,
    formikSetFieldValueRef,
  });

  // Vehicle search filter
  const vehicleSearchFilter = (vehicle, search) => {
    if (!search) return true;
    const term = search.toLowerCase();
    const customerName =
      typeof vehicle.customer === "string"
        ? vehicle.customer
        : vehicle.customer?.name || "";
    return (
      (vehicle.make || vehicle.name || "").toLowerCase().includes(term) ||
      (vehicle.model || "").toLowerCase().includes(term) ||
      (vehicle.tag_number || "").toLowerCase().includes(term) ||
      (vehicle.vin_number || "").toLowerCase().includes(term) ||
      (vehicle.year || "").toLowerCase().includes(term) ||
      customerName.toLowerCase().includes(term)
    );
  };

  // Sort function
  const vehicleSortFunction = (a, b) => {
    const aName = a.make || a.name || "";
    const bName = b.make || b.name || "";
    return aName.localeCompare(bName, undefined, { sensitivity: "base" });
  };

  // Get item name function
  const getVehicleName = (vehicle) =>
    vehicle ? `${vehicle.make || vehicle.name}-${vehicle.model}` : "";

  // Before save transformation
  const onBeforeSave = (values) => {
    return buildVehiclePayload(values, customers, Helpers.authParentId);
  };

  const onAfterFetch = (data) => {
    return data;
  };

  const handleRowClick = (row) => {
    const make = row.make || row.name || "";
    const model = row.model || "";
    const label = `${make} ${model}`.trim() || "vehicle";
    navigate(`/user/receipts/vehicle/${encodeURIComponent(label)}/${row._id}`);
  };

  // Vehicle columns
  const vehicleColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: "year",
      headerName: "Year",
      width: 100,
    },
    {
      field: "make",
      headerName: "Make",
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) => row.make || row.name || "",
    },
    {
      field: "model",
      headerName: "Model",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "vin_number",
      headerName: "VIN Number",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "tag_number",
      headerName: "Tag / Truck #",
      width: 140,
    },
    {
      field: "engine_size",
      headerName: "Engine Size",
      width: 120,
      valueGetter: (value, row) => {
        const num = Number(row.engine_size);
        if (!Number.isFinite(num)) return row.engine_size || "";
        return (Math.round(num * 10) / 10).toFixed(1);
      },
    },
    {
      field: "engine_cylinders",
      headerName: "Cylinders",
      width: 110,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.customer?.name || "",
    },
  ];

  const customActionsRenderer = (
    params,
    canUpdate,
    canDelete,
    handleEdit,
    handleDelete,
  ) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          gap: 1,
        }}
      >
        <IconButton
          onClick={() =>
            navigate("/user/receipts/add", {
              state: {
                prefillVehicle: params.row,
                prefillCustomer: params.row.customer || null,
              },
            })
          }
          color="warning"
          size="small"
          title="Create Estimate"
        >
          <DescriptionIcon fontSize="small" />
        </IconButton>
        {canUpdate && (
          <IconButton
            onClick={() => handleEdit(params.row)}
            color="success"
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {canDelete && (
          <IconButton
            onClick={() => handleDelete(params.row)}
            color="error"
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };


  return (
    <ModuleLayout
      moduleName="Vehicles"
      moduleKey="vehicles"
      icon={require("../images/icons/cars.png")}
      initialFormValues={vehicleInit}
      formFields={vehicleFields}
      validationSchema={vehicleValidationSchema}
      mapItemToFormValues={(item) =>
        prepareVehicleFormValues(item, Helpers.authParentId)
      }
      columns={vehicleColumns}
      searchFilter={vehicleSearchFilter}
      sortFunction={vehicleSortFunction}
      getItemName={getVehicleName}
      customActionsRenderer={customActionsRenderer}
      onBeforeSave={onBeforeSave}
      onAfterFetch={onAfterFetch}
      onRowClick={handleRowClick}
      deleteWarning="This action cannot be undone. All associated records will remain but will no longer be linked to this vehicle."
      moduleDescription="Manage your vehicle inventory and customer associations"
    />
  );
}

export default Vehicles;
